import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { AppShell } from "@/components/AppShell";
import { Linkify } from "@/components/Linkify";
import { PageHero } from "@/components/PageHero";
import { EmptyState } from "@/components/ui/empty-state";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GifPicker } from "@/components/ui/gif-picker";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ProBadge } from "@/components/ui/pro-badge";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  listConversations,
  listMessages,
  sendMessage,
  deleteMessage,
  deleteConversation,
  startConversation,
  listChattableFriends,
} from "@/lib/chat.functions";
import { supabase } from "@/integrations/supabase/client";
import {
  MessageSquare,
  Send,
  Trash2,
  MoreHorizontal,
  UserPlus,
  Users,
  Search,
  ArrowLeft,
} from "lucide-react";

const searchSchema = z.object({ c: z.string().optional() });

export const Route = createFileRoute("/chat")({
  head: () => ({ meta: [{ title: "Mensagens — Viny Club" }] }),
  validateSearch: searchSchema,
  component: Chat,
});

function Chat() {
  const { user, isAuthenticated } = useCurrentUser();
  const { c: activeFromUrl } = Route.useSearch();
  const navigate = Route.useNavigate();
  const fetchConvs = useServerFn(listConversations);
  const fetchMsgs = useServerFn(listMessages);
  const sendFn = useServerFn(sendMessage);
  const delMsgFn = useServerFn(deleteMessage);
  const delConvFn = useServerFn(deleteConversation);
  const qc = useQueryClient();
  const [text, setText] = useState("");

  const convQ = useQuery({
    queryKey: ["conversations"],
    queryFn: () => fetchConvs(),
    enabled: isAuthenticated,
  });
  const conversations = convQ.data ?? [];
  const [convQuery, setConvQuery] = useState("");
  const visibleConversations = conversations.filter((c) => {
    const q = convQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      c.other.username.toLowerCase().includes(q) ||
      (c.other.display_name ?? "").toLowerCase().includes(q)
    );
  });
  const activeId = activeFromUrl ?? conversations[0]?.id;
  const active = conversations.find((c) => c.id === activeId);


  const msgQ = useQuery({
    queryKey: ["messages", activeId],
    queryFn: () => fetchMsgs({ data: { conversationId: activeId! } }),
    enabled: !!activeId,
  });

  useEffect(() => {
    if (!activeId) return;
    const ch = supabase
      .channel(`messages:${activeId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "messages", filter: `conversation_id=eq.${activeId}` },
        () => {
          qc.invalidateQueries({ queryKey: ["messages", activeId] });
          qc.invalidateQueries({ queryKey: ["conversations"] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [activeId, qc]);

  const send = useMutation({
    mutationFn: (vars: { body?: string; mediaUrl?: string } = {}) =>
      sendFn({
        data: {
          conversationId: activeId!,
          body: vars.body ?? text,
          mediaUrl: vars.mediaUrl,
          mediaType: vars.mediaUrl ? "gif" : undefined,
        },
      }),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["messages", activeId] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMsg = useMutation({
    mutationFn: (id: string) => delMsgFn({ data: { messageId: id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", activeId] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delConv = useMutation({
    mutationFn: (id: string) => delConvFn({ data: { conversationId: id } }),
    onSuccess: () => {
      toast.success("Conversa excluída");
      qc.invalidateQueries({ queryKey: ["conversations"] });
      navigate({ search: {} });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!isAuthenticated) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-bold">Entre para usar o chat</h1>
          <Link
            to="/auth"
            className="mt-6 inline-flex rounded-full gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
          >
            Entrar
          </Link>
        </div>
      </AppShell>
    );
  }

  // On mobile, show list OR chat (WhatsApp-style single-pane); URL `?c=` drives it.
  const mobileShowChat = !!activeFromUrl;

  return (
    <AppShell>
      <div className="hidden lg:block">
        <PageHero
          eyebrow="Comunidade"
          title="Mensagens"
          subtitle="Converse com colecionadores, negocie discos e combine trocas."
        />
      </div>


      <div className="mx-auto max-w-7xl px-0 lg:px-8 lg:py-8">
        <div className="grid h-[calc(100dvh-56px-72px-env(safe-area-inset-bottom))] grid-cols-1 overflow-hidden bg-surface lg:h-[calc(100vh-9rem)] lg:grid-cols-[320px_1fr] lg:rounded-2xl lg:border lg:border-border">
          {/* Conversations list */}
          <aside
            className={`min-h-0 flex-col border-border lg:flex lg:border-r ${
              mobileShowChat ? "hidden lg:flex" : "flex"
            }`}
          >
            <div className="sticky top-0 z-10 border-b border-border bg-surface/95 px-4 py-3 backdrop-blur">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-bold">Conversas</h2>
                <FriendsSheet
                  onSelect={(convId) => {
                    qc.invalidateQueries({ queryKey: ["conversations"] });
                    navigate({ search: { c: convId } });
                  }}
                />
              </div>
              {conversations.length > 3 && (
                <div className="relative mt-3">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={convQuery}
                    onChange={(e) => setConvQuery(e.target.value)}
                    placeholder="Buscar conversa"
                    className="h-10 w-full rounded-full border border-input bg-background pl-9 pr-3 text-base focus:border-primary focus:outline-none sm:text-sm"
                  />
                </div>
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {visibleConversations.length === 0 ? (
                <div className="p-6">
                  <EmptyState
                    title={conversations.length === 0 ? "Nenhuma conversa" : "Nada encontrado"}
                    description={
                      conversations.length === 0
                        ? "Encontre pessoas que você segue e comece uma conversa."
                        : "Tente outro nome de colecionador."
                    }
                    icons={[<MessageSquare key="m" />, <Users key="u" />]}
                  />
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {visibleConversations.map((c) => (

                    <div
                      key={c.id}
                      className={`group relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                        c.id === activeId ? "lg:bg-surface-elevated" : "hover:bg-surface-elevated/50 active:bg-surface-elevated"
                      }`}
                    >
                      <button
                        onClick={() => navigate({ search: { c: c.id } })}
                        className="flex flex-1 items-center gap-3 text-left"
                      >
                        <img loading="lazy" decoding="async"
                          src={
                            c.other.avatar_url ||
                            `https://api.dicebear.com/7.x/initials/svg?seed=${c.other.username}`
                          }
                          alt=""
                          className="h-12 w-12 shrink-0 rounded-full object-cover"
                        />
                        <div className="min-w-0 flex-1">
                          <span className="flex items-center gap-1 truncate text-[15px] font-semibold">
                            {c.other.display_name || c.other.username}
                            {c.other.is_premium && <ProBadge size="xs" />}
                          </span>
                          <div className="truncate text-sm text-muted-foreground">
                            {c.last_message?.body ?? "Comece a conversar"}
                          </div>
                        </div>
                      </button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            className="rounded-full p-1.5 text-muted-foreground hover:bg-background hover:text-foreground focus:opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                            aria-label="Opções"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-40 p-1">
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-destructive hover:bg-surface"
                            onClick={() => {
                              if (confirm("Excluir esta conversa e todas as mensagens?")) {
                                delConv.mutate(c.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Excluir conversa
                          </button>
                        </PopoverContent>
                      </Popover>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Chat pane */}
          <section
            className={`min-h-0 flex-col ${
              mobileShowChat
                ? "fixed inset-0 z-50 flex bg-background lg:static lg:z-auto lg:bg-transparent"
                : "hidden lg:flex"
            }`}
          >
            {active ? (
              <>
                <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-surface/95 px-3 py-2.5 backdrop-blur lg:px-4 lg:py-3">
                  <button
                    type="button"
                    onClick={() => navigate({ search: {} })}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full hover:bg-surface-elevated lg:hidden"
                    aria-label="Voltar"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <Link
                    to="/profile/$username"
                    params={{ username: active.other.username }}
                    className="flex min-w-0 flex-1 items-center gap-3"
                  >
                    <img loading="lazy" decoding="async"
                      src={
                        active.other.avatar_url ||
                        `https://api.dicebear.com/7.x/initials/svg?seed=${active.other.username}`
                      }
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-full object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1 truncate font-semibold">
                        {active.other.display_name || active.other.username}
                        {active.other.is_premium && <ProBadge size="xs" />}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">@{active.other.username}</div>
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm("Excluir esta conversa e todas as mensagens?")) {
                        delConv.mutate(active.id);
                      }
                    }}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-surface-elevated hover:text-destructive"
                    aria-label="Excluir conversa"
                    title="Excluir conversa"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-contain bg-background/40 px-3 py-4 lg:px-4">
                  {(msgQ.data ?? []).length === 0 ? (
                    <div className="grid h-full place-items-center text-center text-sm text-muted-foreground">
                      Nenhuma mensagem ainda. Diga oi 👋
                    </div>
                  ) : (
                    (msgQ.data ?? []).map((m) => {
                      const mine = m.sender_id === user?.id;
                      return (
                        <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"}`}>
                          {mine && (
                            <button
                              onClick={() => {
                                if (confirm("Excluir esta mensagem?")) delMsg.mutate(m.id);
                              }}
                              className="mr-1.5 self-center rounded-full p-1 text-muted-foreground hover:bg-surface hover:text-destructive lg:opacity-0 lg:group-hover:opacity-100"
                              aria-label="Excluir mensagem"
                              title="Excluir"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <div
                            className={`max-w-[85%] px-3 py-1.5 text-[15px] shadow-sm lg:max-w-sm ${
                              mine
                                ? "rounded-2xl rounded-br-md bg-primary text-primary-foreground"
                                : "rounded-2xl rounded-bl-md bg-surface-elevated"
                            }`}
                          >
                            {(m as any).media_url && (
                              <img
                                src={(m as any).media_url}
                                alt=""
                                loading="lazy"
                                className="mb-1 max-h-64 rounded-lg"
                              />
                            )}
                            {m.body && <div className="break-words"><Linkify text={m.body} /></div>}
                            <div
                              className={`mt-0.5 text-right text-[10px] ${
                                mine ? "text-primary-foreground/70" : "text-muted-foreground"
                              }`}
                            >
                              {new Date(m.created_at).toLocaleTimeString("pt-BR", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (text.trim()) send.mutate({});
                  }}
                  className="flex items-end gap-2 border-t border-border bg-surface px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] lg:px-3 lg:py-3"
                >
                  <GifPicker onPick={(g) => send.mutate({ mediaUrl: g.url })} />
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Mensagem"
                    className="h-11 min-w-0 flex-1 rounded-full border border-input bg-background px-4 text-base focus:border-primary focus:outline-none sm:text-sm"
                  />
                  <button
                    type="submit"
                    disabled={send.isPending || !text.trim()}
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-full gradient-primary text-primary-foreground disabled:opacity-50"
                    aria-label="Enviar"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              </>
            ) : (
              <div className="grid flex-1 place-items-center p-6">
                <EmptyState
                  title="Selecione uma conversa"
                  description="Ou encontre pessoas que você segue no botão acima."
                  icons={[<MessageSquare key="m" />]}
                />
              </div>
            )}
          </section>
        </div>
      </div>
    </AppShell>
  );
}

function FriendsSheet({ onSelect }: { onSelect: (conversationId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listFn = useServerFn(listChattableFriends);
  const startFn = useServerFn(startConversation);
  const friendsQ = useQuery({
    queryKey: ["chat-friends"],
    queryFn: () => listFn(),
    enabled: open,
  });

  const start = useMutation({
    mutationFn: (userId: string) => startFn({ data: { otherUserId: userId } }),
    onSuccess: ({ conversationId }) => {
      setOpen(false);
      onSelect(conversationId);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const filtered = (friendsQ.data ?? []).filter((f) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      f.username.toLowerCase().includes(q) ||
      (f.display_name ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-surface-elevated"
        >
          <UserPlus className="h-3.5 w-3.5" /> Nova
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full max-w-md flex-col p-0">
        <SheetHeader className="border-b border-border px-5 py-4">
          <SheetTitle>Pessoas que você segue</SheetTitle>
          <SheetDescription>Comece uma conversa com quem já faz parte da sua rede.</SheetDescription>
        </SheetHeader>
        <div className="border-b border-border p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome ou @"
              className="h-10 w-full rounded-full border border-border bg-background pl-9 pr-3 text-base sm:text-sm focus:border-primary focus:outline-none"
            />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {friendsQ.isLoading ? (
            <div className="space-y-3 p-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded-xl bg-surface-elevated/50" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-5">
              <EmptyState
                title={query ? "Ninguém encontrado" : "Você ainda não segue ninguém"}
                description={
                  query
                    ? "Tente outro nome ou usuário."
                    : "Explore a comunidade e siga colecionadores para conversar aqui."
                }
                icons={[<Users key="u" />]}
              />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-5 py-3">
                  <img loading="lazy" decoding="async"
                    src={
                      f.avatar_url ||
                      `https://api.dicebear.com/7.x/initials/svg?seed=${f.username}`
                    }
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 truncate text-sm font-semibold">
                      {f.display_name || f.username}
                      {f.is_premium && <ProBadge size="xs" />}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">@{f.username}</div>
                  </div>
                  <button
                    type="button"
                    disabled={start.isPending}
                    onClick={() => start.mutate(f.id)}
                    className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    Conversar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
